import { ElementRef, Injectable } from '@angular/core'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { PermissionStore } from '@engineering11/access-web'
import { ConfigStore, IFeatures } from '@engineering11/config-web'
import { CustomerPermissions } from '@engineering11/customer-web'
import { ReportingPermissions } from '@engineering11/reporting-web'
import { INavigationItem, e11MenuItemWithRouter, e11MenuItemWithTemplateKey } from '@engineering11/ui-lib/e11-nav-left'
import { ViewportService } from '@engineering11/web-utilities'
import { BehaviorSubject, Observable, combineLatest, map, of } from 'rxjs'

import { CnectFeature } from 'shared-lib'
import { ExtendedNavId } from '../components/layout/main-layout.interface'
import { AllPermissions, Permission } from '../config/permission.config'
import { GatedSettingsCategory, SETTINGS_CATALOG, SettingsCategory } from './settings-catalog'

export const ROUTES = Object.freeze({
  HOME: '/home',
  DASHBOARD: '/dashboard',
  JOBS: '/jobs/list',
  JOBS_BASE: '/jobs',
  JOB_POST: '/jobs/post',
  JOB_PROFILE: '/jobs/profile',
  JOB_CREATE: '/jobs/create',
  JOBS_BOOST: '/jobs/promotions',
  JOBS_TEMPLATE: '/jobs/template/list',
  JOBS_NOT_FOUND: '/jobs/404',
  APPLICATIONS: '/jobs/applications',
  CANDIDATE_POOL: '/candidate-pool/search',
  CANDIDATE_POOL_BASE: '/candidate-pool/',
  CANDIDATE_POOL_MESSAGE: '/candidate-pool/candidates',
  MANAGE_USERS: '/users',
  CLIENT_MANAGEMENT: '/customer-clients',
  COMPANY_ACCOUNT: '/settings/company-account',
  COMPANY_LOGO: '/settings/logo',
  COMPANY_HERO_SECTION: '/settings/hero',
  COMPANY_CONTENT: '/settings/content',
  COMPANY_DISCLAIMER: '/settings/disclaimer',
  COMPANY_LOCATIONS: '/settings/locations',
  COMPANY_DEPARTMENTS: '/settings/departments',
  COMPANY_JOB_AND_APPLICATION: '/settings/job-and-application-settings',
  HIRING_TEAM_SETTINGS: '/account/hiring-team-settings',
  SOCIAL_MEDIA: '/settings/social',
  IQ_LIBRARY: '/settings/interview-questions',
  COMPANY_COMPLIANCE: '/settings/compliance',
  COMPANY_AUTOMATION_SETTINGS: '/settings/automation-settings',
  COMPANY_JOB_APPLICATION_SETTINGS: '/settings/job-application-settings',
  COMPANY_CAREERS: '/settings/careers',
  ACCOUNT: '/account',
  LOGIN: '/auth/login',
  PROMOTIONS: '/jobs/promotions',
  USER_PERSONAL: '/account',
  USER_PIC: '/account/profile-pic',
  USER_SECURITY: '/account/security',
  USER_NOTIFICATIONS: '/account/notifications',
  USER_VIDEO: '/account/profile-video',
  NOT_FOUND: '/home/404',
  REPORTS: '/reports',
  SETTINGS: '/settings',
  // Stub routes — see app-routing.module.ts STUB_ROUTES and docs/unimplemented-pages.md
  STUB_DASHBOARD: '/dashboard',
  STUB_COMPANY_PROFILE: '/settings/company-profile',
  STUB_MARKETING_CHANNELS: '/settings/marketing-channels',
  // CNCT-6818 — Email groups: named lists of addresses a company can email an opening to.
  EMAIL_GROUPS: '/settings/email-groups',
  STUB_ORG_ACCESS: '/settings/personnel/access',
  STUB_TEAM: '/settings/personnel/team',
  STUB_ORG_CHARTS: '/settings/personnel/org-charts',
  STUB_MESSAGING_CHANNELS: '/settings/messaging-channels',
  STUB_AI_CHAT: '/settings/ai-chat',
  JOBS_MANAGE: '/jobs/manage',
  CAMPAIGNS: '/campaigns',
  STUB_ENGAGE_PIPELINE: '/engagement/pipeline',
  STUB_ENGAGE_PROSPECTS: '/engagement/prospects',
  STUB_PIPELINE_CONFIG: '/engagement/settings/pipeline',
  // The Settings tab on each section: a mini hub of the SETTINGS_CATALOG entries in
  // that group, so a setting keeps one home (Company Settings) and the tab is a
  // shortcut to it rather than a second copy.
  OPENINGS_SETTINGS: '/jobs/settings',
  ENGAGE_SETTINGS: '/engagement/settings',
  MESSAGES: '/messages',
})

export const CAREERS_NAV_ITEM = [ROUTES.COMPANY_CONTENT, ROUTES.COMPANY_LOGO, ROUTES.COMPANY_HERO_SECTION, ROUTES.SOCIAL_MEDIA]
export const FEATURE_FLAGGED_NAV = {
  EmployerUserProfileVideo: 'employerUserProfileVideo',
  InterviewQuestionLibrary: 'employerInterviewQuestions',
  EmployerJobPromotions: 'employerJobPromotions',
  CandidatePool: 'candidatePool',
  JobPostAutomation: 'employerJobAutomation',
  EmployerReporting: 'employerReporting',
}

export enum NavItemNames {
  Home = 'Home',
  Dashboard = 'Dashboard',
  Jobs = 'Jobs',
  Openings = 'Openings',
  OpeningsManage = 'Manage',
  Promotions = 'Promotions',
  JobPromotions = 'Opening Promotions',
  JobTemplates = 'Templates',
  Templates = 'Templates',
  Applications = 'Applicant Search',
  JobApplicationSettings = 'Opening & Application Settings',
  Campaigns = 'Campaigns',
  CampaignsDashboard = 'Campaigns Dashboard',
  CampaignsInternal = 'Internal',
  Engage = 'Pipelines',
  EngagePipeline = 'Pipelines',
  EngageProspects = 'Prospects',
  EngageSettings = 'Settings',
  EngageQuestionLibrary = 'Question Library',
  EnginePipelineConfig = 'Pipeline Configuration',
  EngageAutomation = 'Automation Settings',
  CandidatePool = 'Candidate Pool',
  TalentPool = 'Talent Pool',
  CPSearch = 'Candidate Search',
  CPMessages = 'Candidate Messages',
  ManageUsers = 'Users',
  CareersPage = 'Careers Page',
  Website = 'Website',
  SiteContent = 'Site Content',
  LogoAndVideo = 'Logo and Video',
  HeroSection = 'Hero Section',
  SocialMedia = 'Social Media',
  Company = 'Company',
  CompanyProfile = 'Profile',
  CompanyDepartments = 'Departments',
  CompanyLocations = 'Locations',
  CompanyMarketingChannels = 'Marketing Channels',
  CompanyPersonnel = 'Personnel',
  CompanyPersonnelAccount = 'Account',
  CompanyPersonnelTeam = 'Team',
  CompanyPersonnelOrgCharts = 'Org Charts',
  CompanyPersonnelOrgAccess = 'Organization Access',
  CompanySettings = 'Settings',
  CompanyCompliance = 'Compliance',
  CompanyMessagingChannels = 'Messaging Channels',
  CompanyAiChat = 'AI Chat Support',
  CompanyAccount = 'Company Settings',
  HiringTeamSettings = 'Decision Team Settings',
  ClientManagement = 'Client Management',
  UserAccount = 'My Account',
  FileNotFound = 'Oops! Page Not Found',
  Reports = 'Reports',
  Reporting = 'Reporting',
  Message = 'Message',
  Messages = 'Messages',
}

export enum CandidateNavigationSource {
  List = 'list',
  Search = 'search',
}

export interface INavItems {
  name: NavItemNames
  altParent?: Array<string>
  path: string
  icon: string
  children?: Array<{
    label: string
    path: string
    queryParams?: Params
    featureFlagged?: string
    permission?: string
  }>
}

/** Shared with the custom Messages row (MessagingNavComponent), which renders its own star */
export const MESSAGES_FAVORITE_ID = 'messages'

/** CNCT-6764: `favoriteId` marks a leaf favoritable (persisted, so keep ids stable); `secondaryName` is its label in the
 *  Favorites section. `tourId` is the onboarding tour's `[data-tour-id]` anchor, rendered by the nav components. */
export interface IEmployerNavigationItem extends INavigationItem {
  favoriteId?: string
  secondaryName?: string
  tourId?: string
  /**
   * CNCT-6779: overrides a leaf's active highlight when the row owns more routes than
   * its own (Company owns the whole settings context). Mirrors the existing `isOpen`
   * predicate on groups. Absent => the router's exact-route match, unchanged.
   */
  isActive?: (currentUrl: string) => boolean
  children?: IEmployerNavigationItem[]
}

export enum CandidateDetailTabs {
  Evaluation = 'Evaluation',
  Application = 'Application',
  Notes = 'Notes / Files',
  RequestMoreInfo = 'Request Info',
  DirectMessage = 'Direct Message',
  Dialogues = 'Interview Sessions',
  ApplicationHistory = 'Application History',
  Discussion = 'Discussion',
  Feedback = 'Feedback',
}

export enum CandidatePoolTabs {
  Profile = 'Profile',
  DirectMessage = 'Direct Message',
}

/**
 * Strict path-prefix match. A URL is "owned by" a prefix when the URL equals the
 * prefix, OR continues with `/` after the prefix. Query strings are stripped.
 *
 * This replaces the older loose `^${segment}(\/|\?|$|\S+)` pattern which falsely
 * matched things like `/customer` against `/customer-clients/...`.
 */
const matchesPrefix = (prefix: string, url: string): boolean => {
  const u = url.toLowerCase().split('?')[0].replace(/\/$/, '')
  const p = prefix.toLowerCase().replace(/\/$/, '')
  return u === p || u.startsWith(p + '/')
}

/** Route-ownership for a parent nav item. Used by each parent's `isOpen` function. */
interface RouteOwnership {
  /** URL is owned if it equals this path or continues with `/`. */
  prefixes?: string[]
  /** URL is owned only when it equals this path exactly (no descendants). */
  exactPaths?: string[]
}

const ownedBy = (ownership: RouteOwnership, url: string): boolean => {
  const stripped = url.toLowerCase().split('?')[0].replace(/\/$/, '')
  for (const exact of ownership.exactPaths ?? []) {
    if (stripped === exact.toLowerCase().replace(/\/$/, '')) return true
  }
  for (const p of ownership.prefixes ?? []) {
    if (matchesPrefix(p, url)) return true
  }
  return false
}

/**
 * URL-ownership of each parent under the new cnect-ux IA. Re-homed routes are
 * counted under their NEW parent, not their URL prefix.
 *
 * Examples of re-homed routes:
 *   /customer/job-application-settings → Openings (was Company)
 *   /customer/interview-questions      → Engage (was Company)
 *   /customer/automation-settings      → Engage (was Company)
 *   /jobs/promotions                   → Campaigns (was Openings)
 */
/**
 * Routes the Company row owns for its active highlight. CNCT-6779 moved every company
 * settings screen under `/settings`, so that prefix does the work and this only has to
 * name the one catalog destination still living outside it: Manage Users at `/users`.
 *
 * Client Management is deliberately absent — it keeps a top-level nav row of its own, so
 * `/customer-clients` must light THAT row rather than this one.
 */
const COMPANY_OWNERSHIP: RouteOwnership = {
  prefixes: [ROUTES.MANAGE_USERS],
}

const OPENINGS_OWNERSHIP: RouteOwnership = {
  // /jobs/list is owned by Engage (per the 2026-05-22 decision). Openings owns the
  // rest of the /jobs/* tree (record CRUD operations); /jobs/manage is its landing
  // destination and /jobs/template and /jobs/settings are its other two tabs.
  // /jobs/applications moved to ENGAGE_OWNERSHIP when Applicant Search became an
  // Engage tab.
  prefixes: ['/jobs/manage', '/jobs/template', '/jobs/settings', '/jobs/post', '/jobs/profile', '/jobs/create', '/jobs/edit'],
}

const ENGAGE_OWNERSHIP: RouteOwnership = {
  // Pipeline (under Engage) owns /jobs/list — cnect-web's existing Jobs list IS
  // the active-hiring pipeline view (decided 2026-05-22). Openings > Manage is
  // a separate stub at /openings/manage.
  // CNCT-6779 removed the Settings sub-group, so /customer/interview-questions and
  // /customer/automation-settings are no longer reached from under Engage.
  prefixes: ['/engagement', '/candidate-pool', '/jobs/list', '/jobs/applications'],
}

/**
 * URL-ownership maps for the secondary-column extended-nav panels, keyed by
 * `ExtendedNavId` via `isUrlOwnedByExtendedNav`. `MainLayoutComponent` uses these
 * to (a) restore the correct panel on a deep-link/refresh and (b) keep the active
 * panel open while the user navigates within its owned surface, clearing it otherwise.
 */
const REPORTS_OWNERSHIP: RouteOwnership = {
  // Preserves the legacy `url.startsWith(ROUTES.REPORTS)` reset behavior exactly.
  prefixes: [ROUTES.REPORTS],
}

// Messaging owns no URL: its panel is opened by an explicit toggle (not navigation)
// and, per the legacy reset, is cleared on the next navigation. Empty prefixes keep
// `ownedBy` false so that byte-for-byte behavior is preserved.
const MESSAGING_OWNERSHIP: RouteOwnership = {
  prefixes: [],
}

// The settings hub co-owns every catalog route (so the rail restores on a direct
// deep-link/refresh to any setting screen) plus the `/settings` landing itself.
const SETTINGS_OWNERSHIP: RouteOwnership = {
  prefixes: [...new Set(SETTINGS_CATALOG.map(cat => cat.route)), ROUTES.SETTINGS],
}

/**
 * Routes that OPEN the Settings rail on arrival, as opposed to merely keeping an
 * already-open one up (that is `SETTINGS_OWNERSHIP`).
 *
 * CNCT-6779: this used to be the `/settings` landing alone, because a settings screen
 * reached directly still had its own left-nav row — the hub was an explicit destination,
 * not ambient context. Condensing the Company subtree took those rows away, so landing on
 * a settings screen with no rail (a refresh, a bookmark, a shared link) now leaves the
 * user with no way to move between settings at all.
 *
 * Client Management is excluded: it keeps a top-level nav row of its own, so
 * `/customer-clients` is a first-class destination rather than a settings screen in this
 * sense — arriving there should not pull up the hub. `navigation.service.spec` asserts
 * this exclusion stays in step with the nav tree, so putting any catalog route back into
 * the nav will fail loudly rather than quietly hijack it.
 */
const SETTINGS_AUTO_OPEN_OWNERSHIP: RouteOwnership = {
  prefixes: [...new Set(SETTINGS_CATALOG.filter(cat => cat.route !== ROUTES.CLIENT_MANAGEMENT).map(cat => cat.route)), ROUTES.SETTINGS],
}

@Injectable({ providedIn: 'root' })
export class EmployerNavigationService {
  candidateNavigationSource: BehaviorSubject<CandidateNavigationSource> = new BehaviorSubject<CandidateNavigationSource>(
    CandidateNavigationSource.List
  )
  candidateNavigationSourceObservable$ = this.candidateNavigationSource.asObservable()

  userPermissions$ = this.permissionStore.userPermissions$

  userHasPermission$ = (permission: AllPermissions) =>
    this.userPermissions$.pipe(map(permissions => (permissions ? permissions.has(permission) : false)))

  features$ = (feature: keyof IFeatures) => this.configStore.features$.pipe(map(features => (features ? features[feature] : false)))

  /**
   * Story 2.2a: combine the existing `isVisible` observable with the named
   * CnectFeature read from `ConfigStore.features$` (the remote tenant-bootstrap
   * config — same source as every other employer feature flag, e.g.
   * `employerJobPromotions`, `employerInterviewQuestions`). Result emits true
   * only when both the permission/feature check passes AND the flag is ON.
   *
   * Used to enforce the hide-don't-disable rule on the 14 new-IA leaves whose
   * surfaces are flag-gated (see `feature-flag.guard.ts`).
   */
  private gated = (feature: CnectFeature, isVisible$: Observable<boolean> = of(true)): Observable<boolean> =>
    combineLatest([isVisible$, this.features$(feature)]).pipe(map(([visible, flagOn]) => visible && flagOn))

  /**
   * Builds the visibility gate for a hub catalog entry: AND-combines each of the
   * entry's `permissions` with its `feature` flag (when present). This equals the
   * destination route's EFFECTIVE guard (parent AND child), which is authored into
   * `SETTINGS_CATALOG` — NOT the corresponding nav leaf's gate. `of(true)` when the
   * entry has neither a permission nor a flag.
   */
  private gateFor = (cat: SettingsCategory): Observable<boolean> => {
    const gates: Observable<boolean>[] = (cat.permissions ?? []).map(permission => this.userHasPermission$(permission))
    if (cat.feature) gates.push(this.features$(cat.feature))
    if (gates.length === 0) return of(true)
    return combineLatest(gates).pipe(map(flags => flags.every(Boolean)))
  }

  /**
   * Hub-only, permission/feature-gated view of `SETTINGS_CATALOG`, consumed by the
   * settings rail, overview grid, and search. Each entry carries an `isVisible$`
   * equal to its route's effective guard. This does NOT feed the left-nav tree.
   */
  readonly settingsCategories: GatedSettingsCategory[] = SETTINGS_CATALOG.map(cat => ({
    ...cat,
    isVisible$: this.gateFor(cat),
  }))

  /**
   * Destinations that exist only as a tab on a section screen.
   *
   * Collapsing Openings and Engage to single leaves moved Templates, Applicant Search, Talent Pool
   * and the two Settings screens out of the nav tree and onto `section-tabs`. Each still carries a
   * favourite star in its `screen-header`, but the Favorites section resolves an id by walking the
   * nav tree and then `SETTINGS_CATALOG` — so starring one of these wrote the id and then rendered
   * nothing, because there was no row anywhere to resolve it to. This is that third source.
   *
   * Built once and held: `favoriteItems$` maps over this on every favourites emission, and a getter
   * handing back fresh `isVisible$` observables would give the section's async pipes a new source to
   * resubscribe to each time.
   *
   * Ids are persisted in `employer_nav_favorites`, so keep them stable.
   */
  readonly screenFavorites: IEmployerNavigationItem[] = [
    {
      name: 'Templates',
      secondaryName: 'Opening Templates',
      icon: 'content_copy',
      route: ROUTES.JOBS_TEMPLATE,
      isVisible: this.userHasPermission$(Permission.ManageJobTemplates),
      favoriteId: 'openings-templates',
    },
    {
      name: 'Settings',
      secondaryName: 'Opening Settings',
      icon: 'tune',
      route: ROUTES.OPENINGS_SETTINGS,
      isVisible: this.userHasPermission$(Permission.ManageCompanySettings),
      favoriteId: 'openings-settings',
    },
    {
      name: 'Applicant Search',
      secondaryName: 'Applicant Search',
      icon: 'search',
      route: ROUTES.APPLICATIONS,
      isVisible: this.userHasPermission$(Permission.ViewApplications),
      favoriteId: 'engage-applicant-search',
    },
    {
      name: 'Talent Pool',
      secondaryName: 'Talent Pool',
      icon: 'groups',
      route: ROUTES.CANDIDATE_POOL,
      isVisible: combineLatest([
        this.userHasPermission$(Permission.ViewCandidatePool),
        this.userHasPermission$(Permission.ManageCandidatePoolShares),
      ]).pipe(map(([view, share]) => view || share)),
      favoriteId: 'engage-talent-pool',
    },
    {
      name: 'Settings',
      secondaryName: 'Pipelines Settings',
      icon: 'settings',
      route: ROUTES.ENGAGE_SETTINGS,
      isVisible: this.userHasPermission$(Permission.ManageCompanySettings),
      favoriteId: 'engage-settings',
    },
  ] as IEmployerNavigationItem[]

  /**
   * Static cnect-ux 7-item navigation tree, mirrored fully into cnect-web.
   *
   * Top-level items (7) + 2 orphans, in render order:
   *
   *   1. Dashboard                            — STUB
   *   2. Openings        (expandable)         — real (Manage, Templates, J&A Settings)
   *   3. Campaigns       (expandable)         — STUB Dashboard + real Job Promotions + STUB Internal
   *   4. Engage          (expandable)         — STUB Pipelines + Applicant Search + STUB Prospects + real Talent Pool + Settings sub-group
   *   5. Messages        (template-slot)      — real
   *   6. Reporting       (leaf)               — real (gated by EmployerReporting flag + QueryReporting perm)
   *   7. Company         (leaf)               — opens the All Company Settings hub (/settings). The former
   *                                             expandable subtree was condensed per CNCT-6779; its children
   *                                             live in SETTINGS_CATALOG. Sits below Reporting per CNCT-6765.
   *
   *   Orphans (kept unchanged):
   *   8. Client Management                    — real (kept per orphan rule)
   *
   *   (My Account is moved out of the sidebar into the header avatar menu per Story 5.3 — UserAvatarMenuComponent.)
   *
   * Stub leaves point at `/dashboard`, `/company/*`, `/engagement/*` paths
   * wired in `app-routing.module.ts` → `STUB_ROUTES`. Tracker:
   * `cnect-web/docs/unimplemented-pages.md`. Mapping: `docs/nav-inventory.md`.
   */
  readonly navigationItems$: Observable<IEmployerNavigationItem[]> = of<IEmployerNavigationItem[]>([
    // ---------- 1. Dashboard ----------
    {
      name: NavItemNames.Dashboard,
      icon: 'dashboard',
      route: ROUTES.STUB_DASHBOARD,
      isVisible: this.gated('employerDashboard'),
      favoriteId: 'dashboard',
    },

    // ---------- 2. Openings ----------
    // A single leaf, not a group: Manage, Templates and Settings are tabs on the
    // screen itself (`openings-section-tabs`), so the row is one destination rather
    // than a menu you open first. `isActive` keeps it highlighted across every tab
    // and every opening record, the same way the Company leaf does for /settings.
    {
      name: NavItemNames.Openings,
      icon: 'work',
      tourId: 'nav-openings',
      route: ROUTES.JOBS_MANAGE,
      isActive: (currentUrl: string) => ownedBy(OPENINGS_OWNERSHIP, currentUrl),
      isVisible: combineLatest([
        this.userHasPermission$(Permission.ViewJobs),
        this.userHasPermission$(Permission.ManageJobTemplates),
        this.userHasPermission$(Permission.ViewApplications),
      ]).pipe(map(([jobs, templates, apps]) => jobs || templates || apps)),
      favoriteId: 'openings-manage',
      secondaryName: 'Manage Openings',
    },

    // ---------- 3. Campaigns ----------
    // Single top-level leaf — promote openings across connected channels. Gated by its own
    // `employerCampaigns` flag (decoupled from `employerJobPromotions`, which now only gates
    // the legacy /jobs/promotions surface). The former Dashboard/Internal stub children were
    // dropped from the nav when Campaigns collapsed to one item.
    {
      name: NavItemNames.Campaigns,
      icon: 'campaign',
      tourId: 'nav-campaigns',
      route: ROUTES.CAMPAIGNS,
      isVisible: this.gated('employerCampaigns', this.userHasPermission$(Permission.EditPromotions)),
      favoriteId: 'campaigns',
    },

    // ---------- 4. Engage ----------
    // A single leaf for the same reason as Openings: Pipelines, Applicant Search,
    // Talent Pool, Prospects and Settings are tabs on the screen
    // (`engage-section-tabs`). The row lands on Pipelines, which is the existing
    // /jobs/list — cnect-web's jobs list IS the active-hiring pipeline view
    // (decided 2026-05-22).
    {
      name: NavItemNames.Engage,
      icon: 'handshake',
      tourId: 'nav-engage',
      route: ROUTES.JOBS,
      isActive: (currentUrl: string) => ownedBy(ENGAGE_OWNERSHIP, currentUrl),
      isVisible: combineLatest([
        this.userHasPermission$(Permission.ViewCandidatePool),
        this.userHasPermission$(Permission.ManageCandidatePoolShares),
      ]).pipe(map(([view, share]) => view || share)),
      favoriteId: 'engage-pipelines',
      secondaryName: 'Applicant Pipelines',
    },

    // ---------- 5. Messages ----------
    // Template-slot item; Story 4.3 redirects /messages to /candidate-pool/candidates as interim destination.
    // Rendered by `custom-messaging-nav` (stamps `data-tour-id="nav-messages"` and its own pin star); carry the
    // matching tourId so `navTourAnchor` resolves this row during the guided walkthrough. The Favorites entry
    // opens the messaging panel via onClick, matching the row itself (Reports gets the same effect route-driven).
    {
      ...e11MenuItemWithTemplateKey(NavItemNames.Message, 'message', undefined, 'messaging'),
      tourId: 'nav-messages',
      icon: 'mail',
      favoriteId: MESSAGES_FAVORITE_ID,
      // the custom row labels itself "Messages"; keep the Favorites entry consistent
      secondaryName: NavItemNames.Messages,
      onClick: () => this.openMessagesNav(),
    },

    // ---------- 6. Reporting ----------
    {
      name: NavItemNames.Reports,
      icon: 'bar_chart',
      tourId: 'nav-reporting',
      route: ROUTES.REPORTS,
      isVisible: combineLatest([
        this.features$(FEATURE_FLAGGED_NAV.EmployerReporting),
        this.permissionStore.hasPermission(ReportingPermissions.QueryReporting),
      ]).pipe(map(([features, hasPermission]) => features && hasPermission)),
      favoriteId: 'reporting',
    },

    // ---------- 7. Company ----------
    // CNCT-6779: condensed from an expandable group (12 descendants across Website /
    // Personnel / Settings sub-groups) to a single leaf that opens the All Company
    // Settings hub. Every former child is reachable from the hub's grid and its
    // Settings rail, which both render `SETTINGS_CATALOG`; the ones that had no
    // catalog entry were added there in the same change.
    //
    // `isActive` keeps the row lit across the whole settings context — exact-route
    // matching would leave every category page with nothing selected in the menu,
    // which is worse now that this row is the only way in. COMPANY_OWNERSHIP is
    // reused deliberately: it excludes the catalog routes that belong to another
    // top-level item (Openings' J&A Settings, Engage's Question Library /
    // Automation Settings / Pipeline Configuration, and Client Management), so
    // those pages light their own row rather than this one.
    {
      name: NavItemNames.Company,
      icon: 'business',
      tourId: 'nav-company',
      route: ROUTES.SETTINGS,
      isVisible: this.userHasPermission$(Permission.ManageCompanySettings),
      isActive: (currentUrl: string) => ownedBy(COMPANY_OWNERSHIP, currentUrl) || matchesPrefix(ROUTES.SETTINGS, currentUrl),
      favoriteId: 'company-all-settings',
      secondaryName: 'All Company Settings',
    },

    // ---------- (orphan) Client Management ----------
    // Kept unchanged per the orphan rule (no cnect-ux home).
    {
      name: NavItemNames.ClientManagement,
      icon: 'manage_accounts',
      route: ROUTES.CLIENT_MANAGEMENT,
      isVisible: this.userHasPermission$(CustomerPermissions.ViewClient),
      favoriteId: 'client-management',
    },
  ])

  /**
   * Back-compat alias for the existing `MainLayoutComponent.tabsConfig$` consumer.
   * The shell has been updated to consume `navigationItems$` directly; this alias
   * remains until any other consumer is migrated.
   */
  currentMenuStatus$ = this.navigationItems$

  extendedNavContent$ = new BehaviorSubject<ExtendedNavId | undefined>(undefined)

  /**
   * Whether the assistant terminal panel is open. Collapsed (default) shows only a small launcher so the
   * idle footprint is ~zero; expanded shows the full-width resizable terminal. Kept here so the launcher,
   * the header entry, and the panel's own close control can all drive it. Route-independent (it does not
   * participate in the `*_OWNERSHIP` auto-clear-on-navigation logic).
   */
  assistantBarExpanded$ = new BehaviorSubject<boolean>(false)

  mainLayoutScrollableElement?: ElementRef<HTMLDivElement>

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private permissionStore: PermissionStore,
    private configStore: ConfigStore,
    private viewportService: ViewportService
  ) {}

  setExtendedNavContent(id: ExtendedNavId) {
    this.extendedNavContent$.next(id)
  }

  /**
   * Open Messages the way the nav row does: the extended messaging panel on desktop.
   * The panel column doesn't exist below `lg`, so smaller viewports fall back to the
   * interim /messages route (Story 4.3 redirect).
   */
  openMessagesNav(): void {
    const size = this.viewportService.getCurrentSize()
    if (size === 'xs' || size === 'sm' || size === 'md') {
      this.router.navigateByUrl(ROUTES.MESSAGES)
      return
    }
    this.setExtendedNavContent(ExtendedNavId.Messaging)
  }

  resetExtendedNavContent() {
    this.extendedNavContent$.next(undefined)
  }

  /** Open (or collapse) the assistant terminal panel. */
  setAssistantBarExpanded(expanded: boolean) {
    this.assistantBarExpanded$.next(expanded)
  }

  /**
   * Public URL-ownership check for a secondary-column extended-nav panel. Lets
   * `MainLayoutComponent` restore/keep the correct panel without importing the
   * private `ownedBy` / `*_OWNERSHIP` symbols. Returns false for panels that own
   * no URL (e.g. Messaging).
   */
  isUrlOwnedByExtendedNav(id: ExtendedNavId, url: string): boolean {
    const ownership = this.ownershipForExtendedNav(id)
    return ownership ? ownedBy(ownership, url) : false
  }

  /**
   * Whether arriving at this URL should OPEN the Settings rail. True for the hub landing
   * and for every hub-owned settings screen, so a refresh or a deep link onto one still
   * has its navigation — see `SETTINGS_AUTO_OPEN_OWNERSHIP` for why that widened.
   */
  shouldOpenSettingsRailForUrl(url: string): boolean {
    return ownedBy(SETTINGS_AUTO_OPEN_OWNERSHIP, url)
  }

  private ownershipForExtendedNav(id: ExtendedNavId): RouteOwnership | undefined {
    switch (id) {
      case ExtendedNavId.Settings:
        return SETTINGS_OWNERSHIP
      case ExtendedNavId.Reports:
        return REPORTS_OWNERSHIP
      case ExtendedNavId.Messaging:
        return MESSAGING_OWNERSHIP
      default:
        return undefined
    }
  }

  toClientManagement() {
    this.router.navigate([ROUTES.CLIENT_MANAGEMENT])
  }

  toLogin() {
    this.router.navigate([ROUTES.LOGIN])
  }

  toHome() {
    this.router.navigate([ROUTES.HOME])
  }

  toJobNotFound() {
    this.router.navigate([ROUTES.JOBS_NOT_FOUND])
  }

  toNotFound() {
    this.router.navigate([ROUTES.NOT_FOUND])
  }

  toJobPost(jobPostContentId: string) {
    this.router.navigate([ROUTES.JOB_POST, jobPostContentId])
  }

  toJobProfile(jobProfileContentId: string) {
    this.router.navigate([ROUTES.JOB_PROFILE, jobProfileContentId])
  }

  toCandidateDetail(jobId: string, candidateId: string) {
    this.router.navigate(['jobs/candidates/detail/', `${jobId}_${candidateId}`, jobId])
  }

  toCandidateDetailPage(jobId: string, applicationId: string, tab: CandidateDetailTabs = CandidateDetailTabs.Evaluation) {
    return this.router.navigate(['jobs/candidates/detail/', applicationId, jobId], { queryParams: { tab } })
  }

  toCandidateDetailInModal(jobId: string, applicationId: string) {
    const queryParams: Params = { candidateId: applicationId }
    this.router.navigate(['jobs/candidates/', jobId], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
    })
  }

  toCandidatePoolMessage(candidatePoolId: string) {
    return this.router.navigate(['/candidate-pool/candidates/', candidatePoolId])
  }

  toReports(tabId: string) {
    return this.router.navigate([ROUTES.REPORTS], { queryParams: { tab: tabId } })
  }

  toManageCx(jobPostContentId: string) {
    this.router.navigate([ROUTES.JOB_POST, jobPostContentId], { queryParams: { tab: 'Culture Index' } })
  }

  setCandidateNavSource(value: CandidateNavigationSource) {
    this.candidateNavigationSource.next(value)
  }

  addQueryParams(queryParams: Params) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
    })
  }

  updateQueryParams(queryParams: Params) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
    })
  }

  setMainLayoutScrollableElement(nativeElement: ElementRef) {
    this.mainLayoutScrollableElement = nativeElement
  }

  scrollMainLayoutToTop(scrollBehavior?: ScrollBehavior) {
    if (this.mainLayoutScrollableElement) {
      this.mainLayoutScrollableElement.nativeElement.scrollTo({
        top: 0,
        behavior: scrollBehavior,
      })
    }
  }
}
